import React, { useEffect } from "react";
import { Loader2 } from "lucide-react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import TeamBoard from "./pages/TeamBoard";
import EngProjectMap from "./pages/EngProjectMap";
import ProjectHub from "./pages/ProjectHub";
import ProjectSubcontractWorkspace from "./pages/ProjectSubcontractWorkspace";
import CEODashboard from "./pages/CEODashboard";
import AdministrativeApprovalCenter from "./pages/AdministrativeApprovalCenter";
import OfficeSupplies from "./pages/OfficeSupplies";
import ProjectFinanceDetail from "./pages/ProjectFinanceDetail";
import ProjectFinanceList from "./pages/ProjectFinanceList";
import FundsManagement from "./pages/FundsManagement";
import BudgetDefaults from "./pages/BudgetDefaults";
import MyCreditCard from "./pages/finance/MyCreditCard";
import FinImportBatches from "./pages/finance/FinImportBatches";
import Reminders from "./pages/Reminders";
import Import from "./pages/Import";
import AIAssistant from "./pages/AIAssistant";
// 路由兜底页（2026-10-23）：任何未匹配路由不再整页空白，见 pages/NotFound.jsx 顶部注释。
import NotFound from "./pages/NotFound";
import SalesDashboard from "./pages/SalesDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminI18n from "./pages/AdminI18n";
import BranchReport from "./pages/BranchReport";
import NudgeStats from "./pages/NudgeStats";
import PhaseStandards from "./pages/PhaseStandards";
import ProjectImport from "./pages/ProjectImport";
import NudgeReply from "./pages/NudgeReply";
import TradePricingSettings from "./pages/TradePricingSettings";
import NavPlacesSettings from "./pages/admin/NavPlacesSettings";
// 【2026-10-13 新增】系统管理 → 用户管理（角色管理 / 账号列表）+ 分公司管理 + 菜单管理
import RolesManager from "./pages/admin/RolesManager";
import BranchesManager from "./pages/admin/BranchesManager";
import MenuManager from "./pages/admin/MenuManager";
import BranchStaff from "./pages/BranchStaff";
// 【2026-10-20 新增】系统设置 → 使用分析（员工使用时长 / 模块 / 时段 / 活跃衰减 / 排名）。
// 数据范围由后端 usage_scope 收窄：admin/CEO 全公司，经理本部门（销售线含本分公司），员工只自己。
import UsageAnalytics from "./pages/admin/UsageAnalytics";
import CompletedArchive from "./pages/CompletedArchive";
// 二-7：整改看板(RectifyBoard)与异常看板(EngineExceptions)已删除，见 Layout.jsx 注释。
import ChangeOrders from "./pages/ChangeOrders";
import QuickBooksSettings from "./pages/QuickBooksSettings";
import AuditLog from "./pages/AuditLog";
import FinanceCenter from "./pages/FinanceCenter";
import TemporaryMaterialProcurement from "./pages/TemporaryMaterialProcurement";
import AssetCard from "./pages/AssetCard";
import MyMentions from "./pages/MyMentions";
import CallHistory from "./pages/CallHistory";
import AgentBoard from "./pages/AgentBoard";
import AgentDetail from "./pages/AgentDetail";
import CallAnalytics from "./pages/CallAnalytics";
import OutboundList from "./pages/OutboundList";
import GoldenScripts from "./pages/GoldenScripts";
import TMDatabase from "./pages/tm/TMDatabase";
import TMImports from "./pages/tm/TMImports";
import TMInvalid from "./pages/tm/TMInvalid";
import TMSettings from "./pages/tm/TMSettings";
import TMAudit from "./pages/tm/TMAudit";
import TMToday from "./pages/tm/TMToday";
import TMTasks from "./pages/tm/TMTasks";
import TMWorkbench from "./pages/tm/TMWorkbench";
import TMMyLeads from "./pages/tm/TMMyLeads";
import TMB2B from "./pages/tm/TMB2B";
import TMFunnel from "./pages/tm/TMFunnel";
import OwnerSmsTemplates from "./pages/admin/OwnerSmsTemplates";
import AISettings from "./pages/admin/AISettings";
import GoogleMapsSettings from "./pages/admin/GoogleMapsSettings";
// 【2026-10-26 新增】Google 日历 OAuth 凭据配置页（HRMS 走查一-2），与地图配置同档权限。
import GoogleCalendarSettings from "./pages/admin/GoogleCalendarSettings";
import CashModeSettings from "./pages/admin/CashModeSettings";
import SmsConsentLog from "./pages/admin/SmsConsentLog";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminMigration from "./pages/admin/AdminMigration";
import TMQA from "./pages/tm/TMQA";
import Payments from "./pages/Payments";
import RecycleBin from "./pages/RecycleBin";
import PortalEntry from "./pages/PortalEntry";
import InspectionSchedule from "./pages/InspectionSchedule";
import SubcontractorCompanies from "./pages/SubcontractorCompanies";
import SignFollowupBoard from "./pages/SignFollowupBoard";
import PrePublishCheck from "./pages/PrePublishCheck";
// 二-7：异常看板(EngineExceptions)已删除，见 Layout.jsx 注释。
// 二-8：超期预警看板（OverdueBoard）已删除，见 Layout.jsx 注释。
// 二-9：全链路运行状态看板（ChainBoard）已删除，见 Layout.jsx 注释。
import CdcWorkbench from "./pages/CdcWorkbench";
import SiteInspection from "./pages/SiteInspection";
import SiteSurvey from "./pages/SiteSurvey";
import VrShare from "./pages/VrShare";
import SurveyReview from "./pages/SurveyReview";
import SmsTerms from "./pages/legal/SmsTerms";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import SiteInspectionsOverview from "./pages/SiteInspectionsOverview";
import SubCheckinBoard from "./pages/SubCheckinBoard";
import MyToday from "./pages/MyToday";
import DoneReport from "./pages/DoneReport";
import SatisfactionBoard from "./pages/SatisfactionBoard";
import CRMLeads from "./pages/CRMLeads";
import AdMaterialManagement from "./pages/AdMaterialManagement";
import SalesWorkspace from "./pages/SalesWorkspace";
import GlobalLeadDrawer from "./components/crm/GlobalLeadDrawer";
import GlobalProjectDrawer from "./components/GlobalProjectDrawer";
import CRMDashboard from "./pages/CRMDashboard";
import CRMReviewQueue from "./pages/CRMReviewQueue";
import CRMLostAnalytics from "./pages/CRMLostAnalytics";
import CRMHallOfFame from "./pages/CRMHallOfFame";
import CRMPool from "./pages/CRMPool";
import CRMAutomations from "./pages/CRMAutomations";
import OwnerDesignConfirm from "./pages/OwnerDesignConfirm";
import CRMOverdue from "./pages/CRMOverdue";
import CRMUnsignedContracts from "./pages/CRMUnsignedContracts";
import CRMSignedCustomers from "./pages/CRMSignedCustomers";
import PaymentCalendar from "./pages/PaymentCalendar";
import FinanceBatchCollect from "./pages/FinanceBatchCollect";
import PayeeLedger from "./pages/PayeeLedger";
import CRMEvalInvites from "./pages/CRMEvalInvites";
import PriceApprovals from "./pages/PriceApprovals";
import DesignReports from "./pages/design/DesignReports";
import DesignV3Dashboard from "./pages/design/v3/DesignV3Dashboard";
import DesignV3Projects from "./pages/design/v3/DesignV3Projects";
import DesignPresign from "./pages/design/v3/DesignPresign";
import DesignPresignDetail from "./pages/design/v3/DesignPresignDetail";
import { DesignChangesPage } from "./components/DesignRevisionTasks";
import DesignV3Tasks from "./pages/design/v3/DesignV3Tasks";
import DesignTodo from "./pages/design/v3/DesignTodo";
import DesignV3Approvals from "./pages/design/v3/DesignV3Approvals";
import DesignV3SignDates from "./pages/design/v3/DesignV3SignDates";
import DesignV3Team from "./pages/design/v3/DesignV3Team";
import DesignV3Automation from "./pages/design/v3/DesignV3Automation";
import DesignV3AI from "./pages/design/v3/DesignV3AI";
import DesignV3Settings from "./pages/design/v3/DesignV3Settings";
const LegacyDesignProjectRedirect = () => { const { pid } = useParams(); return <Navigate to={`/design/projects/${pid}?tab=stages`} replace />; };
import OwnerDesignPortal from "./pages/OwnerDesignPortal";
import DesignSharePortal from "./pages/DesignSharePortal";
import PlanSignPortal from "./pages/PlanSignPortal";
import SubcontractSignPortal from "./pages/SubcontractSignPortal";
import SubcontractDualSigning from "./pages/SubcontractDualSigning";
import SubcontractLinkSigning from "./pages/SubcontractLinkSigning";
import DesignAppointment from "./pages/DesignAppointment";
import DesignPayPage from "./pages/DesignPayPage";
import InvoicePayPage from "./pages/InvoicePayPage";
import SubCheckinPage from "./pages/SubCheckinPage";
import SubScheduleWorker from "./pages/SubScheduleWorker";
import WeeklyRecapPage from "./pages/WeeklyRecapPage";
import SubRfqPage from "./pages/SubRfqPage";
import StructOrderPage from "./pages/StructOrderPage";
import CollabTaskPage from "./pages/CollabTaskPage";
import Collab from "./pages/Collab";
import ChatHub from "./pages/ChatHub";
import ChatPopout from "./pages/ChatPopout";
import { isPopoutWindow, sendToMain } from "./lib/commPopout";
import DesignV3StructOrders from "./pages/design/v3/DesignV3StructOrders";
import ConceptDesignPortal from "./pages/ConceptDesignPortal";
import Todos from "./pages/Todos";
import SharePhotoWall from "./pages/SharePhotoWall";
import PlanShareView from "./pages/PlanShareView";
import ContractVerify from "./pages/ContractVerify";
import ReceiptVerify from "./pages/ReceiptVerify";
import CheckVerify from "./pages/CheckVerify";
import ScoVerify from "./pages/ScoVerify";
import ScoSignPortal from "./pages/ScoSignPortal";
import LienVerify from "./pages/LienVerify";
import LienSignPortal from "./pages/LienSignPortal";
import CoVerify from "./pages/CoVerify";
import ProposalShare from "./pages/ProposalShare";
import SurveyConfirm from "./pages/SurveyConfirm";
import OwnerPortal from "./pages/OwnerPortal";
import SubOnboard from "./pages/SubOnboard";
import EvalAck from "./pages/EvalAck";
import OwnerSurvey from "./pages/OwnerSurvey";
import TaskLinkPage from "./pages/TaskLinkPage";
import EstimateView from "./pages/EstimateView";
import QuotesCompare from "./pages/QuotesCompare";
import DesignQuoteView from "./pages/DesignQuoteView";
import PermitStatusView from "./pages/PermitStatusView";
import MaterialInventory from "./pages/MaterialInventory";
import Warehouses from "./pages/Warehouses";
import ScanWorkbench from "./pages/ScanWorkbench";
import WhBinRecheck from "./pages/WhBinRecheck";
import WarehouseDashboard from "./pages/WarehouseDashboard";
import HRMSDashboard from "./pages/hrms/HRMSDashboard";
import Announcements from "./pages/hrms/Announcements";
import HRMSOrg from "./pages/hrms/HRMSOrg";
import HRMSEmployees from "./pages/hrms/HRMSEmployees";
import HRMSAssets from "./pages/hrms/HRMSAssets";
import HRMSTransitions from "./pages/hrms/HRMSTransitions";
import HRMSAttendance from "./pages/hrms/HRMSAttendance";
import HRMSSchedule from "./pages/hrms/HRMSSchedule";
import HRMSLeaves from "./pages/hrms/HRMSLeaves";
import HRMSPlanning from "./pages/hrms/HRMSPlanning";
import HRMSComingSoon from "./pages/hrms/HRMSComingSoon";
import HRMSRecruiting from "./pages/hrms/HRMSRecruiting";
import HRMSCompensation from "./pages/hrms/HRMSCompensation";
import HRMSPerformance from "./pages/hrms/HRMSPerformance";
import HRMSBenefits from "./pages/hrms/HRMSBenefits";
import HRMSCnPayroll from "./pages/hrms/HRMSCnPayroll";
import HRMSTalent from "./pages/hrms/HRMSTalent";
import HRMSTraining from "./pages/hrms/HRMSTraining";
import InternalKnowledgeBase from "./pages/InternalKnowledgeBase";
import HRMSSafety from "./pages/hrms/HRMSSafety";
import HRMSMyHR from "./pages/hrms/HRMSMyHR";
import HRMSRelations from "./pages/hrms/HRMSRelations";
import OfferSign from "./pages/OfferSign";
import OnboardingPortal from "./pages/OnboardingPortal";
import HRMSOnboarding from "./pages/hrms/HRMSOnboarding";
import HRMSUsPayroll from "./pages/hrms/HRMSUsPayroll";
import InterviewPack from "./pages/InterviewPack";
import Careers from "./pages/Careers";
import CareersStatus from "./pages/CareersStatus";
import WarehouseAudit from "./pages/WarehouseAudit";
import QuoteView from "./pages/QuoteView";
import ContractSign from "./pages/ContractSign";
import IntentSignPage from "./pages/IntentSignPage";
import ReferralProgram from "./pages/ReferralProgram";
import AftersalesCustomers from "./pages/AftersalesCustomers";
import AssistantLog from "./pages/AssistantLog";
import PartnerApply from "./pages/referral/PartnerApply";
import ReferralLanding from "./pages/referral/ReferralLanding";
import PartnerPortal from "./pages/referral/PartnerPortal";
import { Toaster } from "./components/ui/sonner";
import UploadProgressStack from "./components/UploadProgressStack";
import SystemUpdateNotice from "./components/SystemUpdateNotice";
import { isAllowedNavPath, defaultLanding } from "./lib/navAccess";
import { startUploadRunner } from "./lib/uploadRunner";

function Guard({ children }) {
  const { user } = useApp();
  const loc = useLocation();
  /**
   * 启动后台上传 worker（原图补传队列，2026-10-20）。
   *
   * 需求来源（用户）：「后台上传应该退出页面也能继续…只要不关闭项目吧…」
   *
   * 为什么挂在 Guard 里：这里**一定是已登录状态**（没有 user 直接跳登录页），
   * 而 worker 需要 token 才能上传。挂在这里也意味着「只要还停留在系统里」——
   * 切菜单、换项目、关掉某个卡片都不会中断补传；刷新页面后 App 重新挂载，
   * worker 会重新扫 IndexedDB 里没传完的任务接着传。
   *
   * 必须在 `if (!user)` 之前调用也无妨（worker 内部会判断未登录直接返回），
   * 但放在 Hook 区最上方更符合规则：Hook 不能写在 return 之后。
   */
  React.useEffect(() => { startUploadRunner(); }, [user?.username]);
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

/* 独立聊天窗口（window.name=ali_chat_popout）里点到非聊天链接 → 转交主页面打开，自己回到聊天 */
function PopoutRedirect({ children }) {
  const loc = useLocation();
  if (isPopoutWindow()) { sendToMain({ type: "nav", path: loc.pathname + loc.search }); return <Navigate to="/chat-popout" replace />; }
  return children;
}

function AdminCeoOnly({ children, to = "/design/dashboard" }) {
  const { user } = useApp();
  if (!["admin", "ceo"].includes(user?.role)) return <Navigate to={to} replace />;
  return children;
}

/**
 * 设计管理配置页访问守卫。
 *
 * 需求背景：自动化中心与集成设置里的规则阈值、流程挂载、机构文件模板，
 * 后端均允许 `admin / ceo / design_manager` 修改，但路由此前只放行前两者。
 * 结果是设计主管能在审批中心触发自动化，却进不了配置页；如果仅放宽菜单，
 * 深链仍会被重定向，权限仍然不一致。
 *
 * 这里按后端同一组管理角色放行，普通设计师仍只能看到只读业务页面。
 */
function DesignManagerOnly({ children, to = "/design/dashboard" }) {
  const { user } = useApp();
  if (!["admin", "ceo", "design_manager"].includes(user?.role)) return <Navigate to={to} replace />;
  return children;
}

/* 角色锁定模块：项目经理不可进入仓库管理相关页面（与 AdminUsers 锁定复选框一致） */
function NoPm({ children }) {
  const { user } = useApp();
  if (user?.role === "pm") return <Navigate to="/board" replace />;
  return children;
}

function Landing() {
  const { user, api } = useApp();
  const cacheKey = user ? `ali_landing:${user.username}` : null;
  const [pref, setPref] = React.useState(() => { try { return cacheKey ? (localStorage.getItem(cacheKey) ?? undefined) : undefined; } catch { return undefined; } });
  // 【2026-10-28 代操作落地页】管理员「以此账号登录」时，直接进目标账号的**角色默认页**，
  // 不读该账号存的「登录首页」偏好，也不去请求它的 prefs。
  // 【为什么】老板要求：「代操作账号时就直接进入原本的账号的默认页，不要之前 admin 的浏览页面记录」。
  //   改造前这里会按 `ali_landing:<目标账号>`（本地缓存）或服务端 prefs 决定落地页 —— 而这两个来源
  //   都可能带着「上一次操作者浏览到的页面」的痕迹（代操作期间 Layout 也会以为自己是目标账号，
  //   把浏览过的页面写进目标账号的最近访问 / 登录首页），代操作者一进去就落在别人上次停留的地方。
  //   代操作是「临时以别人身份看他的工作台」，语义上就该从他自己岗位的主页开始。
  // 【取舍】代操作期间完全忽略偏好落点（即使目标账号自己设过首页）；退出代操作回到管理员账号后，
  //   管理员自己的偏好照常生效（那时 user.impersonatedBy 已为空）。
  const impersonating = !!user?.impersonatedBy;
  React.useEffect(() => {
    if (!user || impersonating || pref !== undefined) return;
    let alive = true;
    api.get("/users/me/prefs").then(({ data }) => { if (!alive) return; const l = data?.landing || null; try { if (l) localStorage.setItem(cacheKey, l); } catch {} setPref(l); }).catch(() => alive && setPref(null));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, impersonating]);
  if (!user) return <Navigate to="/login" replace />;
  if (impersonating) {
    const own = defaultLanding(user);
    if (own) return <Navigate to={own} replace />;
    return <Dashboard />;
  }
  if (pref === undefined) return <div className="flex items-center justify-center py-24 text-slate-400" data-testid="landing-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  // 存的登录首页必须「当前账号确实可见」才认；否则回落到角色默认首页。
  // 历史 bug：切换账号后沿用了上个账号存的 landing（如 admin 的 /admin/ai），落到本账号无权限的页面。
  const storedOk = !!pref && pref.startsWith("/") && pref !== "/" && isAllowedNavPath(pref);
  const target = storedOk ? pref : defaultLanding(user);
  if (target) return <Navigate to={target} replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          {/* 版本提示挂在全局路由内：切换页面仍保留「稍后更新」选择，刷新后重新检查。 */}
          <SystemUpdateNotice />
          {/* 全局「客户线索详情」抽屉：挂一次、全站可用。
              它同时负责把「从别的页面进入 /crm/leads/:id」重写成「来源页 + ?lead=<id>」，
              这样从转介绍 / 通话记录 / 客户公海等页面点客户时，抽屉就地弹出、不再切走菜单。
              详见 components/crm/GlobalLeadDrawer.jsx 顶部注释。 */}
          <GlobalLeadDrawer />
          {/* 全局「项目详情」抽屉：挂一次、全站可用（2026-10-15，参考已签约客户详情抽屉化）。
              同时负责把站内任何页面进入 /project/:id（含 /inspection、/site-survey 子路由）
              重写成「来源页 + ?project=<id>[&inspect=1|&survey=1]」，抽屉就地弹出；
              子页面按多级抽屉叠层。详见 components/GlobalProjectDrawer.jsx 顶部注释。 */}
          <GlobalProjectDrawer />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/share/:token" element={<SharePhotoWall />} />
            <Route path="/plan/:token" element={<PlanShareView />} />
            <Route path="/verify/:code" element={<ContractVerify />} />
            <Route path="/verify-receipt/:code" element={<ReceiptVerify />} />
            <Route path="/verify-check/:code" element={<CheckVerify />} />
            <Route path="/verify-sco/:code" element={<ScoVerify />} />
            <Route path="/verify-lien/:code" element={<LienVerify />} />
            <Route path="/verify-co/:code" element={<CoVerify />} />
            <Route path="/esign/offer/:token" element={<OfferSign />} />
            <Route path="/onboard/:token" element={<OnboardingPortal />} />
            <Route path="/hr/interview-pack/:token" element={<InterviewPack />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/job/:jobId" element={<Careers />} />
            <Route path="/careers/status/:token" element={<CareersStatus />} />
            <Route path="/proposal/:token" element={<ProposalShare />} />
            <Route path="/survey-confirm/:token" element={<SurveyConfirm />} />
            <Route path="/owner/:token" element={<OwnerPortal />} />
            <Route path="/nudge/:token" element={<NudgeReply />} />
            <Route path="/sub-onboard/:token" element={<SubOnboard />} />
            <Route path="/eval-ack/:token" element={<EvalAck />} />
            <Route path="/owner-survey/:token" element={<OwnerSurvey />} />
            <Route path="/task/:token" element={<TaskLinkPage />} />
            <Route path="/quote/:token" element={<QuoteView />} />
            <Route path="/estimate/:token" element={<EstimateView />} />
            <Route path="/quotes/:token" element={<QuotesCompare />} />
            <Route path="/design-quote/:token" element={<DesignQuoteView />} />
            <Route path="/permit-status/:token" element={<PermitStatusView />} />
            <Route path="/contract-sign/:token" element={<ContractSign />} />
            <Route path="/sign/intent/:token" element={<IntentSignPage />} />
            <Route path="/partner/apply" element={<PartnerApply />} />
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/partner/portal/:code" element={<PartnerPortal />} />
            <Route path="/design-confirm/:token" element={<OwnerDesignConfirm />} />
            <Route path="/owner/design/:token" element={<OwnerDesignPortal />} />
            <Route path="/appt/:token" element={<DesignAppointment />} />
            <Route path="/pay/design/:token" element={<DesignPayPage />} />
            <Route path="/pay/invoice/:token" element={<InvoicePayPage />} />
            <Route path="/s/checkin/:token" element={<SubCheckinPage />} />
            <Route path="/s/work/:token" element={<SubScheduleWorker />} />
            <Route path="/w/:share" element={<WeeklyRecapPage />} />
            <Route path="/s/rfq/:token" element={<SubRfqPage />} />
            <Route path="/s/struct/:token" element={<StructOrderPage />} />
            <Route path="/s/task/:token" element={<CollabTaskPage />} />
            <Route path="/vr/:token" element={<VrShare />} />
            <Route path="/survey-review/:token" element={<SurveyReview />} />
            <Route path="/sms-terms" element={<SmsTerms />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/concept/:token" element={<ConceptDesignPortal />} />
            <Route path="/share/design/:token" element={<DesignSharePortal />} />
            <Route path="/sign/plans/:token" element={<PlanSignPortal />} />
            <Route path="/sign/subcontract/:token" element={<SubcontractSignPortal />} />
            <Route path="/sign/subcontract-link/:token" element={<SubcontractLinkSigning />} />
            <Route path="/subcontracts/:sid/signing" element={<Guard><SubcontractDualSigning /></Guard>} />
            <Route path="/sign/sco/:token" element={<ScoSignPortal />} />
            <Route path="/sign/lien/:token" element={<LienSignPortal />} />
            <Route path="/chat-popout" element={<Guard><ChatPopout /></Guard>} />
            <Route path="/chat-popout/c/:channelId" element={<Guard><ChatPopout /></Guard>} />
            <Route path="/chat-popout/m/:messageId" element={<Guard><ChatPopout /></Guard>} />
            <Route element={<Guard><PopoutRedirect><Layout /></PopoutRedirect></Guard>}>
              <Route path="/" element={<Landing />} />
              <Route path="/overview" element={<Dashboard />} />
              <Route path="/board" element={<TeamBoard />} />
              <Route path="/projects/map" element={<EngProjectMap />} />
              <Route path="/branch-report/:branch" element={<BranchReport />} />
              <Route path="/nudge-stats" element={<NudgeStats />} />
              <Route path="/settings/phase-standards" element={<PhaseStandards />} />
              <Route path="/settings/project-import" element={<ProjectImport />} />
              <Route path="/project/:id" element={<ProjectHub />} />
              <Route path="/project/:id/subcontracts" element={<ProjectSubcontractWorkspace />} />
              <Route path="/finance" element={<Navigate to="/finance/projects" replace />} />
              <Route path="/ceo" element={<CEODashboard />} />
              <Route path="/ceo/administration/*" element={<AdministrativeApprovalCenter />} />
              <Route path="/administration/office-supplies" element={<OfficeSupplies />} />
              <Route path="/reminders" element={<Reminders />} />
              <Route path="/import" element={<Import />} />
              <Route path="/ai" element={<AIAssistant />} />
              <Route path="/sales" element={<Navigate to="/crm/leads?tab=perf" replace />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/i18n" element={<AdminI18n />} />
              <Route path="/admin/owner-sms" element={<OwnerSmsTemplates />} />
              <Route path="/admin/ai" element={<AISettings />} />
              <Route path="/admin/google-maps" element={<AdminCeoOnly><GoogleMapsSettings /></AdminCeoOnly>} />
              {/* Google 日历凭据与地图配置同档：全公司级外部集成，仅 admin / ceo 可进。 */}
              <Route path="/admin/google-calendar" element={<AdminCeoOnly><GoogleCalendarSettings /></AdminCeoOnly>} />
              {/* 深链也必须验证管理员角色；密码解锁由页面和后端共同执行。 */}
              <Route path="/admin/cash-mode" element={<AdminCeoOnly><CashModeSettings /></AdminCeoOnly>} />
              <Route path="/admin/sms-consent" element={<SmsConsentLog />} />
              {/* 需求反馈汇总页（仅 admin/ceo）—— 与 Layout NAVDEF、server.py MODULE_CATALOG 三处同步 */}
              <Route path="/admin/feedback" element={<AdminFeedback />} />
              <Route path="/admin/migration" element={<AdminMigration />} />
              <Route path="/admin/trade-pricing" element={<TradePricingSettings />} />
              <Route path="/admin/nav-places" element={<NavPlacesSettings />} />
              <Route path="/admin/usage" element={<UsageAnalytics />} />
              {/* 【2026-10-13 新增】用户管理的三级菜单 + 分公司管理 / 菜单管理（侧栏 NAVDEF 与后端 MODULE_CATALOG 已同步加入对应 key） */}
              <Route path="/admin/roles" element={<RolesManager />} />
              <Route path="/admin/branches" element={<BranchesManager />} />
              <Route path="/branch-staff" element={<BranchStaff />} />
              <Route path="/admin/menus" element={<MenuManager />} />
              <Route path="/archive" element={<CompletedArchive />} />
              {/* 二-7：/rectify（整改看板）路由已移除 */}
              <Route path="/change-orders" element={<ChangeOrders />} />
              <Route path="/quickbooks" element={<QuickBooksSettings />} />
              <Route path="/audit-log" element={<AuditLog />} />
              <Route path="/finance-center" element={<FinanceCenter />} />
              <Route path="/temp-material-procurement" element={<TemporaryMaterialProcurement />} />
              <Route path="/assets/:aid" element={<AssetCard />} />
              <Route path="/finance/project/:pid" element={<ProjectFinanceDetail />} />
              <Route path="/finance/projects" element={<ProjectFinanceList />} />
              <Route path="/finance/imports" element={<FinImportBatches />} />
              <Route path="/finance/funds" element={<FundsManagement />} />
              <Route path="/finance/budget-defaults" element={<BudgetDefaults />} />
              <Route path="/my-mentions" element={<MyMentions />} />
              <Route path="/calls" element={<CallHistory />} />
              <Route path="/agents" element={<AgentBoard />} />
              <Route path="/agents/:username" element={<AgentDetail />} />
              <Route path="/call-analytics" element={<CallAnalytics />} />
              <Route path="/outbound-list" element={<OutboundList />} />
              <Route path="/golden-scripts" element={<GoldenScripts />} />
              <Route path="/tm/homeowners" element={<TMDatabase key="homeowners" type="homeowners" />} />
              <Route path="/tm/realtors" element={<TMDatabase key="realtors" type="realtors" />} />
              <Route path="/tm/imports" element={<TMImports />} />
              <Route path="/tm/invalid" element={<TMInvalid />} />
              <Route path="/tm/settings" element={<TMSettings />} />
              <Route path="/tm/audit" element={<TMAudit />} />
              <Route path="/tm/today" element={<TMToday />} />
              <Route path="/tm/tasks" element={<TMTasks />} />
              <Route path="/tm/workbench" element={<TMWorkbench />} />
              <Route path="/tm/my-leads" element={<TMMyLeads />} />
              <Route path="/tm/b2b" element={<TMB2B />} />
              <Route path="/tm/funnel" element={<TMFunnel />} />
              <Route path="/tm/qa" element={<TMQA />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/recycle-bin" element={<RecycleBin />} />
              <Route path="/portal/*" element={<PortalEntry />} />
              <Route path="/inspection-schedule/:id" element={<InspectionSchedule />} />
              <Route path="/subcontractor-companies" element={<SubcontractorCompanies />} />
              <Route path="/sign-followup" element={<SignFollowupBoard />} />
              <Route path="/admin/pre-publish" element={<PrePublishCheck />} />
              {/* 二-7：/engine-exceptions（异常看板）路由已移除 */}
              {/* 二-8：/overdue-board 路由已移除（超期预警看板删除） */}
              {/* 二-9：/chain-board 路由已移除（全链路运行状态看板删除） */}
              <Route path="/online-pm" element={<CdcWorkbench />} />
              <Route path="/cdc" element={<Navigate to="/online-pm" replace />} />
              <Route path="/site-inspections" element={<SiteInspectionsOverview />} />
              <Route path="/sub-checkin-board" element={<SubCheckinBoard />} />
              <Route path="/project/:id/inspection" element={<SiteInspection />} />
              <Route path="/project/:id/site-survey" element={<SiteSurvey />} />
              <Route path="/my-today" element={<MyToday />} />
              <Route path="/done-report" element={<DoneReport />} />
              <Route path="/satisfaction" element={<SatisfactionBoard />} />
              <Route path="/inventory" element={<MaterialInventory />} />
              <Route path="/warehouses" element={<NoPm><Warehouses /></NoPm>} />
              <Route path="/warehouses/scan" element={<NoPm><ScanWorkbench /></NoPm>} />
              <Route path="/warehouses/bin/:bid" element={<NoPm><WhBinRecheck /></NoPm>} />
              <Route path="/warehouse-dashboard" element={<NoPm><WarehouseDashboard /></NoPm>} />
              <Route path="/hrms" element={<HRMSDashboard />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/hrms/org" element={<HRMSOrg />} />
              <Route path="/hrms/employees" element={<HRMSEmployees />} />
              <Route path="/hrms/assets" element={<HRMSAssets />} />
              <Route path="/hrms/transitions" element={<HRMSTransitions />} />
              <Route path="/hrms/attendance" element={<HRMSAttendance />} />
              <Route path="/hrms/schedule" element={<HRMSSchedule />} />
              <Route path="/hrms/my-schedule" element={<HRMSSchedule mine />} />
              <Route path="/finance/my-card" element={<MyCreditCard />} />
              <Route path="/hrms/leaves" element={<HRMSLeaves />} />
              <Route path="/hrms/planning" element={<HRMSPlanning />} />
              <Route path="/hrms/recruiting" element={<HRMSRecruiting />} />
              <Route path="/hrms/compensation" element={<HRMSCompensation />} />
              <Route path="/hrms/performance" element={<HRMSPerformance />} />
              <Route path="/hrms/benefits" element={<HRMSBenefits />} />
              <Route path="/hrms/cn-payroll" element={<HRMSCnPayroll />} />
              <Route path="/hrms/us-payroll" element={<HRMSUsPayroll />} />
              <Route path="/hrms/onboarding" element={<HRMSOnboarding />} />
              <Route path="/hrms/talent" element={<HRMSTalent />} />
              <Route path="/hrms/training" element={<HRMSTraining />} />
              <Route path="/hrms/safety" element={<HRMSSafety />} />
              <Route path="/hrms/me" element={<HRMSMyHR />} />
              <Route path="/hrms/relations" element={<HRMSRelations />} />
              <Route path="/hrms/m/:module" element={<HRMSComingSoon />} />
              <Route path="/warehouse-audit" element={<NoPm><WarehouseAudit /></NoPm>} />
              <Route path="/crm/leads" element={<SalesWorkspace />} />
              <Route path="/crm/ad-materials" element={<AdMaterialManagement />} />
              <Route path="/crm/construction" element={<Navigate to="/board" replace />} />{/* 2026-06 施工项目(销售) 已并入工程项目 */}
              {/* 需求（2026-10-13）：客户线索详情不再单独跳页，改为在列表页上叠抽屉展示。
                  做法对齐设计项目（/design/projects 与 /design/projects/:pid 同渲染列表页，有 pid 就叠抽屉）：
                  这里让 :id 也渲染 SalesWorkspace，由它读 useParams 决定是否叠出抽屉。
                  好处：深链 / 刷新 / 分享链接全部照常可用，站内 10+ 处 /crm/leads/:id 的旧入口无需改动。 */}
              <Route path="/crm/leads/:id" element={<SalesWorkspace />} />
              <Route path="/crm/dashboard" element={<CRMDashboard />} />
              <Route path="/crm/mm-review" element={<CRMReviewQueue />} />
              <Route path="/crm/lost-analytics" element={<CRMLostAnalytics />} />
              <Route path="/crm/hall-of-fame" element={<CRMHallOfFame />} />
              <Route path="/crm/overdue" element={<CRMOverdue />} />
              <Route path="/crm/contracts/unsigned" element={<CRMUnsignedContracts />} />
              <Route path="/crm/signed" element={<CRMSignedCustomers />} />
              {/* 需求（2026-10-13）：已签约客户详情同样改为抽屉，理由与 /crm/leads/:id 一致。
                  注意：本页筛选状态存在 URL search（cat / due / 各类筛选键），列表页打开抽屉时会带上
                  当前 search 并在关闭时还原，保证「关掉抽屉回到的还是同一个筛选后的列表」。 */}
              <Route path="/crm/signed/:id" element={<CRMSignedCustomers />} />
              <Route path="/crm/payment-calendar" element={<PaymentCalendar />} />
              <Route path="/finance/batch-collect" element={<FinanceBatchCollect />} />
              <Route path="/finance/payees" element={<PayeeLedger />} />
              <Route path="/referral" element={<ReferralProgram />} />
              <Route path="/aftersales/customers" element={<AftersalesCustomers />} />
              {/* 老客户详情沿用 CRM 的 URL 驱动抽屉，刷新、分享和关闭后回到原筛选均可用。 */}
              <Route path="/aftersales/customers/:id" element={<AftersalesCustomers />} />
              <Route path="/crm/assistant-log" element={<AssistantLog />} />
              <Route path="/crm/eval-invites" element={<CRMEvalInvites />} />
              <Route path="/crm/pool" element={<CRMPool />} />
              <Route path="/crm/automations" element={<CRMAutomations />} />
              <Route path="/crm/price-approvals" element={<PriceApprovals />} />
              <Route path="/crm/site-visits" element={<Navigate to="/crm/leads" replace />} />
              <Route path="/design/projects" element={<DesignV3Projects />} />
              <Route path="/design/projects/:pid" element={<DesignV3Projects />} />
              <Route path="/design/changes/:pid" element={<DesignChangesPage />} />
              <Route path="/design/presign" element={<DesignPresign />} />
              {/* 详情改为抽屉展示（2026-10-20 用户要求「参考已签约用户详情」）：
                  与 /crm/signed/:id 同一套做法 —— 详情路由也渲染**列表页**，由列表页根据 :lid
                  在列表之上叠出 CrmDetailDrawer（内容仍是 DesignPresignDetail）。
                  这样关闭抽屉能回到原来的筛选位置，刷新 / 分享 / 后退都落在「列表 + 抽屉」。
                  详情路由参数是线索 id（新工单入口）；旧通知 / 转化看板跳设计项目 id 也在后端兼容。 */}
              <Route path="/design/presign/:lid" element={<DesignPresign />} />
              {/* 详情组件保留独立可渲染能力（embedded=false 时恢复成整页形态），
                  供「可独立打开的页面 / 打印」等场景复用；正常点击列表行不再走这条路由。 */}
              <Route path="/design/presign/:lid/full" element={<DesignPresignDetail />} />
              <Route path="/design/dashboard" element={<DesignV3Dashboard />} />
              <Route path="/design/tasks" element={<DesignV3Tasks />} />
              <Route path="/design/todo" element={<DesignTodo />} />
              <Route path="/design/approvals" element={<DesignV3Approvals />} />
              <Route path="/design/sign-dates" element={<DesignV3SignDates />} />
              <Route path="/design/team" element={<DesignV3Team />} />
              <Route path="/design/automation" element={<DesignManagerOnly><DesignV3Automation /></DesignManagerOnly>} />
              <Route path="/design/ai" element={<DesignV3AI />} />
              <Route path="/design/settings" element={<DesignManagerOnly><DesignV3Settings /></DesignManagerOnly>} />
              <Route path="/design/legacy/projects" element={<Navigate to="/design/projects" replace />} />
              <Route path="/design/legacy/projects/:pid" element={<LegacyDesignProjectRedirect />} />
              <Route path="/design/legacy/dashboard" element={<Navigate to="/design/dashboard" replace />} />
              <Route path="/design/legacy/import" element={<Navigate to="/design/settings" replace />} />
              <Route path="/design/my-work" element={<Navigate to="/design/tasks" replace />} />
              <Route path="/design/import" element={<Navigate to="/design/settings" replace />} />
              <Route path="/design/reports" element={<DesignReports />} />
              <Route path="/design/struct-orders" element={<DesignV3StructOrders />} />
              <Route path="/todos" element={<Todos />} />
              <Route path="/collab" element={<Collab />} />
              <Route path="/knowledge" element={<InternalKnowledgeBase />} />
              <Route path="/chat" element={<ChatHub />} />
              <Route path="/chat/c/:channelId" element={<ChatHub />} />
              <Route path="/chat/m/:messageId" element={<ChatHub />} />
              {/* 兜底路由（2026-10-23）：必须放在所有具体路由之后、且挂在 Layout 之内。
                  放在 Layout 内是为了让失效路径仍保留左侧导航与顶栏 —— 用户能从坏链接上继续用系统，
                  而不是像之前 /rectify 那样整页空白、连导航都没有。详见 pages/NotFound.jsx。 */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AppProvider>
      {/* 全局右上角提示。
          需求（2026-10-21 用户）：「右上角提醒增加关闭，现在悬浮很久遮挡操作」。
          1) 关闭按钮：由 components/ui/sonner.jsx 默认开启，用户可随时手动关掉，不用等倒计时。
          2) 位置：默认 offset 是 32px，提示会压住 4rem 高的顶栏（铃铛 / 头像就在那里），
             鼠标悬停时 sonner 还会暂停倒计时，于是顶栏按钮被长时间挡住点不到。
             这里把提示整体下移到顶栏下方，即使提示还在，也不会盖住顶栏操作。 */}
      <Toaster
        position="top-right"
        richColors
        offset={{ top: "calc(4rem + env(safe-area-inset-top, 0px) + 12px)", right: "1rem" }}
        mobileOffset={{ top: "calc(4rem + env(safe-area-inset-top, 0px) + 8px)" }}
      />
      {/* 右上角全局上传进度（2026-10-22 用户需求「上传时最好是屏幕右上角有一个上传进度」）。
          进度由 lib/api.js 的请求拦截器统一登记（任何 FormData 上传都会进来），
          这里只负责显示，所以阶段任务卡 / City 结论附件 / 评论附件 / 协作 / 财务导入
          等原本没有进度的入口，现在也都能看到上传在走。
          与上面的 Toaster 同一右上角、同一 offset，浮层整体排在提示卡下方，
          两者不会互相遮挡（详见 components/UploadProgressStack.jsx）。 */}
      <UploadProgressStack />
    </div>
  );
}

export default App;
